import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createSuccessToast } from '@adflow/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import type {
	AddPromotionAssetDto,
	EditPromotionAssetDto,
	ListPromotionAssetsQuery,
} from '@adflow/shared/data-google-ads';
import {
	AddPromotionAsset,
	ApplyPromotionAssetToAdAccounts,
	ApplyPromotionAssetToCampaigns,
	DeletePromotionAsset,
	EditPromotionAsset,
	GetPromotionAsset,
	ListPromotionAssets,
} from '@adflow/shared/data-google-ads';

import { googleAdsAccountKeys } from './google-ads-account-queries';

export const promotionAssetKeys = {
	all: ['google-ads', 'promotion-asset'] as const,
	allList: () => [...promotionAssetKeys.all, 'list'] as const,
	list: (query: ListPromotionAssetsQuery) =>
		[...promotionAssetKeys.allList(), query] as const,
	view: (promotionAssetId: number) =>
		[...promotionAssetKeys.all, 'view', promotionAssetId] as const,
};

export function useListPromotionAssetsQuery(
	query: ListPromotionAssetsQuery,
	enabled = true
) {
	return useQuery({
		queryKey: promotionAssetKeys.list(query),
		queryFn: ({ signal }) =>
			apiRequest(ListPromotionAssets, { query }, { signal }),
		enabled,
	});
}

export function useAddPromotionAssetMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['google-ads', 'common']);
	return useMutation({
		mutationFn: (body: AddPromotionAssetDto) =>
			apiRequest(AddPromotionAsset, { body }),
		onSuccess: (promotionAssetOrError) => {
			if ('error' in promotionAssetOrError) {
				handleRequestErrors([promotionAssetOrError], {
					errorMessage: t('google-ads:error.savingTemplate'),
				});
				return;
			}

			const promotionAsset = promotionAssetOrError;
			queryClient.setQueryData(
				promotionAssetKeys.view(promotionAsset.id),
				promotionAsset
			);
			void queryClient.invalidateQueries({
				queryKey: promotionAssetKeys.allList(),
			});
			createSuccessToast(t('google-ads:success.savingTemplate'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('google-ads:error.savingTemplate'),
			});
		},
	});
}

export function useEditPromotionAssetMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['google-ads', 'common']);
	return useMutation({
		mutationFn: ({
			promotionAssetId,
			edits,
		}: {
			promotionAssetId: number;
			edits: EditPromotionAssetDto;
		}) =>
			apiRequest(EditPromotionAsset, {
				params: { promotionAssetId },
				body: edits,
			}),
		onSuccess: (promotionAssetOrError) => {
			if ('error' in promotionAssetOrError) {
				handleRequestErrors([promotionAssetOrError], {
					errorMessage: t('google-ads:error.savingTemplate'),
				});
				return;
			}

			const promotionAsset = promotionAssetOrError;
			queryClient.setQueryData(
				promotionAssetKeys.view(promotionAsset.id),
				promotionAsset
			);
			void queryClient.invalidateQueries({
				queryKey: promotionAssetKeys.allList(),
			});
			createSuccessToast(t('google-ads:success.savingTemplate'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('google-ads:error.savingTemplate'),
			});
		},
	});
}

export function useDeletePromotionAssetMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['google-ads', 'common']);
	return useMutation({
		mutationFn: (promotionAssetId: number) =>
			apiRequest(DeletePromotionAsset, {
				params: { promotionAssetId },
			}),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: promotionAssetKeys.allList(),
			});
			createSuccessToast(t('google-ads:success.deletingTemplate'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('google-ads:error.deletingTemplate'),
			});
		},
	});
}

export const fetchPromotionAsset = async (promotionAssetId: number) => {
	return apiRequest(GetPromotionAsset, {
		params: { promotionAssetId },
	});
};

export function usePromotionAssetQuery(promotionAssetId: number) {
	return useQuery({
		queryKey: promotionAssetKeys.view(promotionAssetId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetPromotionAsset,
				{ params: { promotionAssetId } },
				{ signal }
			),
		enabled: Boolean(promotionAssetId),
	});
}

export function useApplyPromotionAssetToCampaignsMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['google-ads', 'common']);
	return useMutation({
		mutationFn: ({
			promotionAssetId,
			campaignTemplateIds,
		}: {
			promotionAssetId: number;
			campaignTemplateIds: number[];
		}) =>
			apiRequest(ApplyPromotionAssetToCampaigns, {
				params: { promotionAssetId },
				body: { campaignTemplateIds },
			}),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: promotionAssetKeys.allList(),
			});
			// Invalidate template queries to refresh campaign data
			void queryClient.invalidateQueries({
				queryKey: ['google-ads', 'template'],
			});
			createSuccessToast(t('google-ads:success.applyingPromotionAsset'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('google-ads:error.applyingPromotionAsset'),
			});
		},
	});
}

export function useApplyPromotionAssetToAdAccountsMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['google-ads', 'common']);
	return useMutation({
		mutationFn: ({
			promotionAssetId,
			adAccountAvbIds,
		}: {
			promotionAssetId: number;
			adAccountAvbIds: string[];
		}) =>
			apiRequest(ApplyPromotionAssetToAdAccounts, {
				params: { promotionAssetId },
				body: { adAccountAvbIds },
			}),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: promotionAssetKeys.allList(),
			});
			// Invalidate ad account queries to refresh saved promotional assets
			void queryClient.invalidateQueries({
				queryKey: googleAdsAccountKeys.allList(),
			});
			createSuccessToast(
				t('google-ads:success.applyingPromotionAssetToAdAccounts')
			);
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('google-ads:error.applyingPromotionAssetToAdAccounts'),
			});
		},
	});
}
